import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from './components/ui/provider';
import { Toaster } from './components/ui/toaster';
import { AuthProvider } from './contexts/AuthContext';
import { CustomerProvider } from './contexts/CustomerContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { StudentProvider } from './contexts/StudentContext';
import { FamilyProvider } from './contexts/FamilyContext';
import { ClassProvider } from './contexts/ClassContext';
import { CourseProvider } from './contexts/CourseContext';
import { TutorProvider } from './contexts/TutorContext';
import { UserProvider } from './contexts/UserContext';
import { CacheInvalidationProvider } from './contexts/CacheInvalidationContext';
import Layout from './components/Layout';
import ProtectedRoute from './contexts/ProtectedRoute';
import FeatureFlagRoute from './contexts/FeatureFlagRoute';
import theme from './theme/chakra-theme';
import './theme/colors.css'; // Global color theme overrides
import { Box, Spinner, Text } from '@chakra-ui/react';

// Eager-load pages since they're the entry point
import LoginPage from './pages/LoginPage';

// Lazy-load all other pages for code splitting (40% faster initial load)
const ProfileSelectionPage = lazy(() => import('./pages/ProfileSelectionPage'));
const LeadPage = lazy(() => import('./pages/LeadPage'));
const StudentEnrollmentPage = lazy(() => import('./pages/StudentEnrollmentPage'));
const StudentCheckInPage = lazy(() => import('./pages/StudentCheckInPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AttendancePage = lazy(() => import('./pages/AttendancePage'));
const TutorNotesPage = lazy(() => import('./pages/TutorNotesPage'));
const AdministrationPage = lazy(() => import('./pages/AdministrationPage'));
const ClassManagement = lazy(() => import('./pages/ClassManagement'));
const FamilyManagementPage = lazy(() => import('./pages/FamilyManagementPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const SchedulingManagerPage = lazy(() => import('./pages/SchedulingManagerPage'));
const UltimateScheduling = lazy(() => import('./pages/UltimateScheduling'));
const ParentPortalPage = lazy(() => import('./pages/ParentPortalPage'));
const ParentMessagesPage = lazy(() => import('./pages/ParentMessagesPage'));
const AdminMessagesPage = lazy(() => import('./pages/AdminMessagesPage'));
const TutorSchedulePage = lazy(() => import('./pages/TutorSchedulePage'));
const HomePage = lazy(() => import('./pages/HomePage'));

// Loading fallback component
const PageLoadingFallback = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minH="100vh"
    bg="gray.50"
    gap={4}
  >
    <Spinner size="xl" color="blue.500" thickness="4px" />
    <Text color="gray.600" fontSize="md" fontWeight="500">
      Loading...
    </Text>
  </Box>
);

function App() {
  return (
    <Provider value={theme}>
      <ThemeProvider>
        <AuthProvider>
          <CacheInvalidationProvider>
            <CustomerProvider>
              <StudentProvider>
              <FamilyProvider>
                <ClassProvider>
                  <CourseProvider>
                    <TutorProvider>
                      <UserProvider>
                        <Router>
                        <Layout>
                        <Suspense fallback={<PageLoadingFallback />}>
                    <Routes>
                    <Route path="/login" element={<LoginPage />} />
                <Route 
                  path="/select-profile" 
                  element={
                    <ProtectedRoute>
                      <ProfileSelectionPage />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/student-enrollment"
                  element={
                    <ProtectedRoute>
                      <StudentEnrollmentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/student-checkin"
                  element={
                    <ProtectedRoute>
                      <StudentCheckInPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/scheduling-manager"
                  element={
                    <ProtectedRoute>
                      <SchedulingManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/attendance"
                  element={
                    <ProtectedRoute>
                      <AttendancePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tutor-notes"
                  element={
                    <ProtectedRoute>
                      <TutorNotesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/administration"
                  element={
                    <ProtectedRoute>
                      <AdministrationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/class-management"
                  element={
                    <ProtectedRoute>
                      <ClassManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/families"
                  element={
                    <ProtectedRoute>
                      <FamilyManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute>
                      <ReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/class-scheduling"
                  element={
                    <ProtectedRoute>
                      <SchedulingManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ultimate-scheduling"
                  element={
                    <ProtectedRoute>
                      <UltimateScheduling />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads"
                  element={
                    <ProtectedRoute>
                      <LeadPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/parent-portal"
                  element={
                    <ProtectedRoute>
                      <ParentPortalPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/parent-messages"
                  element={
                    <ProtectedRoute>
                      <ParentMessagesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messages"
                  element={
                    <ProtectedRoute>
                      <FeatureFlagRoute flag="MESSAGES">
                        <AdminMessagesPage />
                      </FeatureFlagRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tutor-schedule"
                  element={
                    <ProtectedRoute>
                      <TutorSchedulePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  }
                />
                    </Routes>
                        </Suspense>
                      </Layout>
                        </Router>
                      </UserProvider>
                    </TutorProvider>
                  </CourseProvider>
                </ClassProvider>
              </FamilyProvider>
            </StudentProvider>
          </CustomerProvider>
          </CacheInvalidationProvider>
        </AuthProvider>
      </ThemeProvider>
      <Toaster />
    </Provider>
  );
}

export default App;
